import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_BASE_URL from '../config';

/** Parse filename from Content-Disposition (RFC 5987 + quoted / unquoted). */
function parseFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== 'string') return null;
  const cd = contentDisposition.trim();
  const star = cd.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"+|"+$/g, ''));
    } catch {
      return null;
    }
  }
  const quoted = cd.match(/filename\s*=\s*"((?:\\.|[^"\\])*)"/i);
  if (quoted && quoted[1] !== undefined) {
    return quoted[1].replace(/\\(.)/g, '$1');
  }
  const quotedSimple = cd.match(/filename\s*=\s*"([^"]*)"/i);
  if (quotedSimple && quotedSimple[1] !== undefined) return quotedSimple[1];
  const unquoted = cd.match(/filename\s*=\s*([^;\s]+)/i);
  if (unquoted && unquoted[1]) {
    return unquoted[1].replace(/^["']|["']$/g, '');
  }
  return null;
}

/** Remove trailing underscores from basename (before extension) — avoids corrupt / odd saves from greedy CD parsing or DB titles. */
function sanitizeDownloadBasename(fileName) {
  if (!fileName || typeof fileName !== 'string') return fileName;
  let n = fileName.trim().replace(/^.*[/\\]/, '');
  const lastDot = n.lastIndexOf('.');
  if (lastDot <= 0) {
    return n.replace(/_+$/g, '').replace(/^_+/g, '') || 'download';
  }
  const base = n.slice(0, lastDot);
  const ext = n.slice(lastDot);
  const cleanBase = base.replace(/_+$/g, '').replace(/^_+/g, '');
  return (cleanBase || 'download') + ext;
}

// Async thunk for fetching all requests with pagination and filters
export const fetchRequests = createAsyncThunk(
  'requests/fetchRequests',
  async ({ page = 1, limit = 10, status = null, featureType = null, projectId = null, countedOnly = false, includeInFlight = false } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      let url = `${API_BASE_URL}/api/requests?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (featureType) url += `&featureType=${featureType}`;
      if (projectId) url += `&projectId=${projectId}`;
      if (countedOnly) url += `&countedOnly=true`;
      if (includeInFlight) url += `&includeInFlight=true`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return {
          requests: response.data.requests || [],
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 0,
          currentPage: response.data.currentPage || 1,
          totalTokens: response.data.totalTokens || 0,
          totalCost: response.data.totalCost || 0,
        };
      }

      return {
        requests: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        totalTokens: 0,
        totalCost: 0,
      };
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch requests';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for fetching a single request by ID
export const fetchRequestById = createAsyncThunk(
  'requests/fetchRequestById',
  async (requestId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/requests/${requestId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.request) {
        return response.data.request;
      }

      return rejectWithValue('Request not found');
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue('Request not found');
      }
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for creating a new request
export const createRequest = createAsyncThunk(
  'requests/createRequest',
  async ({ featureType, requestData, projectId, file = null }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('featureType', featureType);
      formData.append('requestData', JSON.stringify(requestData));
      const normalizedProjectId = Number.parseInt(String(projectId ?? '').trim(), 10);
      if (!Number.isInteger(normalizedProjectId) || normalizedProjectId < 1) {
        return rejectWithValue('Please select a valid project before generating documents.');
      }
      formData.append('projectId', String(normalizedProjectId));
      
      if (file) {
        formData.append('file', file);
      }

      const response = await axios.post(`${API_BASE_URL}/api/requests`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        return response.data;
      }

      return rejectWithValue('Failed to create request');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to create request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for deleting a request
export const deleteRequest = createAsyncThunk(
  'requests/deleteRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/api/requests/${requestId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return requestId;
      }

      return rejectWithValue('Failed to delete request');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to delete request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for resubmitting a request
export const resubmitRequest = createAsyncThunk(
  'requests/resubmitRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/requests/${requestId}/resubmit`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data;
      }

      return rejectWithValue('Failed to resubmit request');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to resubmit request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for downloading a document
export const downloadDocument = createAsyncThunk(
  'requests/downloadDocument',
  async (requestId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/requests/${requestId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', // Important for file download
      });

      // Create a download link and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition =
        response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let fileName =
        parseFilenameFromContentDisposition(contentDisposition) ||
        `request_${requestId}_file.docx`;
      fileName = sanitizeDownloadBasename(fileName);

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { requestId, fileName, success: true };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue('Document not found');
      }
      if (error.response?.status === 403) {
        return rejectWithValue('You do not have permission to download this document');
      }
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to download document';
      return rejectWithValue(errorMsg);
    }
  }
);

const initialState = {
  // All requests list
  allRequests: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
  
  // Single request details
  currentRequest: null,
  
  // User statistics
  totalTokens: 0,
  totalCost: 0,
  
  // UI states
  loading: false,
  error: null,
  success: false,
  
  // Download states
  downloading: false,
  downloadError: null,
  downloadSuccess: false,
  lastDownloadedFileName: null,
  
  // Last created request info
  lastCreatedRequestId: null,
  lastCreatedStatus: null,
};

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.lastCreatedRequestId = null;
      state.lastCreatedStatus = null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
    setCurrentRequest: (state, action) => {
      state.currentRequest = action.payload;
    },
    clearDownloadError: (state) => {
      state.downloadError = null;
    },
    clearDownloadSuccess: (state) => {
      state.downloadSuccess = false;
      state.lastDownloadedFileName = null;
    },
    resetRequestsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch requests
      .addCase(fetchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.allRequests = action.payload.requests;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.totalTokens = action.payload.totalTokens;
        state.totalCost = action.payload.totalCost;
        state.error = null;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch request by ID
      .addCase(fetchRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;
        state.error = null;
      })
      .addCase(fetchRequestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentRequest = null;
      })
      // Create request
      .addCase(createRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastCreatedRequestId = action.payload.requestId;
        state.lastCreatedStatus = action.payload.status;
        state.error = null;
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete request
      .addCase(deleteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.allRequests = state.allRequests.filter(req => req.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resubmit request
      .addCase(resubmitRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(resubmitRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastCreatedRequestId = action.payload.requestId;
        state.lastCreatedStatus = action.payload.status;
        state.error = null;
      })
      .addCase(resubmitRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Download document
      .addCase(downloadDocument.pending, (state) => {
        state.downloading = true;
        state.downloadError = null;
        state.downloadSuccess = false;
      })
      .addCase(downloadDocument.fulfilled, (state, action) => {
        state.downloading = false;
        state.downloadSuccess = true;
        state.lastDownloadedFileName = action.payload.fileName;
        state.downloadError = null;
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.downloading = false;
        state.downloadError = action.payload;
        state.downloadSuccess = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentRequest,
  setCurrentRequest,
  clearDownloadError,
  clearDownloadSuccess,
  resetRequestsState,
} = requestsSlice.actions;

export default requestsSlice.reducer;
